import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import type { FilterOption } from "@outpave/ds";
import { GET_CONTACT_FILTER_OPTIONS } from "graphql/contact";
import ContactStatus from "enums/ContactStatus";
import { contactName } from "utils/fullName";
import { isChildContact } from "utils/contacts/payee";

/**
 * Ceiling on the option list. The DS `SelectFilter` takes static options and
 * searches them locally, so it cannot page: a company past this many active
 * contacts gets a truncated list. Lifting it needs an async SelectFilter in the
 * package, or a server filter for payable contacts, rather than a bigger number here.
 */
const MAX_OPTIONS = 200;

interface ContactFilterNode {
  id: string;
  businessName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  contactType?: string | null;
  parentContactId?: string | null;
}

/**
 * Active contacts a payment can be sent to, sorted by display name. An
 * individual under a business is left out, because a payment goes to the
 * business. Bump `refetchIndex` after a change that may add a payee.
 */
export const useVendorFilterOptions = ({
  skip = false,
  refetchIndex = 0,
}: { skip?: boolean; refetchIndex?: number } = {}): FilterOption[] => {
  const { data, refetch } = useQuery(GET_CONTACT_FILTER_OPTIONS, {
    variables: { first: MAX_OPTIONS, status: [ContactStatus.Active] },
    // Names change and payees are added elsewhere: show what is cached, then ask again.
    fetchPolicy: "cache-and-network",
    skip,
  });

  useEffect(() => {
    if (refetchIndex && !skip) refetch().catch(() => undefined);
  }, [refetchIndex, skip, refetch]);

  return useMemo(() => {
    const edges: { node: ContactFilterNode }[] = data?.contacts?.edges ?? [];
    const named = edges
      .map(({ node }) => node)
      .filter((node) => !isChildContact(node))
      .map((node) => ({
        value: node.id,
        label: contactName(node),
        email: node.email ?? "",
      }))
      .filter((option) => option.label !== "");

    const uses = new Map<string, number>();
    named.forEach(({ label }) => uses.set(label, (uses.get(label) ?? 0) + 1));

    // Two contacts can share a name; the email is what tells them apart.
    return named
      .map(({ value, label, email }) => ({
        value,
        label:
          (uses.get(label) ?? 0) > 1 && email && email !== label
            ? `${label} (${email})`
            : label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);
};

export default useVendorFilterOptions;
